import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

// Video data with external links
const videos = [
    { id: 1, type: 'vertical', platform: 'youtube-shorts', videoId: 'U4Hje_B3-wQ' },
    { id: 2, type: 'vertical', platform: 'youtube-shorts', videoId: 'camkOxnfMGc' },
    { id: 3, type: 'vertical', platform: 'youtube-shorts', videoId: '8eGLeTICmSs' },
    { id: 4, type: 'vertical', platform: 'youtube-shorts', videoId: 'qtqhnDxx6vM' },
    { id: 5, type: 'horizontal', platform: 'gdrive', fileId: '1jE3UA1O_erJucXQkTm-26o9DqkbZGZG_' },
    { id: 6, type: 'horizontal', platform: 'gdrive', fileId: '1ku97eFVFA5KEMbd6mgoXcXDlN7qlubT7' },
    { id: 7, type: 'horizontal', platform: 'gdrive', fileId: '1fbvFBMGePp9S78R2OAQ3mVpMIx2oZCL4' },
    { id: 8, type: 'horizontal', platform: 'gdrive', fileId: '1Xc3AzhLRTpg6mAcUug8VXlzjnDQMxlzh' },
    { id: 9, type: 'horizontal', platform: 'gdrive', fileId: '1wtPahj1CwBz3TBNiBe4jswpROw6AJd5S' },
    { id: 10, type: 'horizontal', platform: 'youtube', videoId: 'JllmpLtJolY' },
    { id: 11, type: 'horizontal', platform: 'youtube', videoId: 'BKB9eVLBMlI' },
    { id: 12, type: 'horizontal', platform: 'youtube', videoId: 'Fl7YGECSH8c' },
];

function getEmbedUrl(video: typeof videos[0]) {
    switch (video.platform) {
        case 'youtube-shorts':
        case 'youtube':
            return `https://www.youtube.com/embed/${video.videoId}?autoplay=0&rel=0`;
        case 'gdrive':
            return `https://drive.google.com/file/d/${video.fileId}/preview`;
        default:
            return '';
    }
}

export function AiContentPage() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const currentVideo = videos[currentIndex];
    const isVertical = currentVideo.type === 'vertical';

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1));
    };

    return (
        <div className="min-h-screen text-white overflow-x-hidden">
            <div className="global-fixed-bg" />
            <div className="relative z-10">
                <Header />

                {/* Fixed Back Button - Responsive positioning */}
                <Link
                    to="/portofolio"
                    className="fixed top-[72px] sm:top-20 left-4 sm:left-6 z-50 inline-flex items-center gap-1.5 sm:gap-2 text-white/90 text-xs sm:text-sm font-medium hover:text-white transition-colors bg-black/60 backdrop-blur-sm px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full border border-white/10 hover:bg-black/80"
                >
                    <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Kembali</span>
                </Link>

                <section className="pt-32 sm:pt-28 pb-16 px-4 sm:px-6">
                    <div className="max-w-5xl mx-auto">
                        {/* Title - Responsive text */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-8 sm:mb-12 text-center"
                        >
                            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-white">
                                AI Content
                            </h1>
                            <p className="text-white/50 mt-3 sm:mt-4 text-sm sm:text-base lg:text-lg max-w-lg mx-auto">
                                Koleksi karya video AI dengan konsistensi karakter tinggi
                            </p>
                        </motion.div>

                        {/* Single Video Slide */}
                        <div className="relative flex items-center justify-center px-10 sm:px-16">
                            {/* Navigation Arrows */}
                            <button
                                onClick={goToPrevious}
                                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 sm:bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all"
                            >
                                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </button>

                            <button
                                onClick={goToNext}
                                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 sm:bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all"
                            >
                                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </button>

                            {/* Video Display - Responsive and aspect ratio aware */}
                            <motion.div
                                key={currentIndex}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3 }}
                                className={`relative overflow-hidden rounded-2xl shadow-2xl ${isVertical
                                        ? 'w-[280px] sm:w-[320px] md:w-[360px] aspect-[9/16]'
                                        : 'w-full max-w-3xl aspect-video'
                                    }`}
                            >
                                <iframe
                                    src={getEmbedUrl(currentVideo)}
                                    className="absolute inset-0 w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    title={`AI Content Video ${currentVideo.id}`}
                                />
                            </motion.div>
                        </div>

                        {/* Video Indicators */}
                        <div className="flex justify-center gap-2 mt-6 sm:mt-8 flex-wrap max-w-md mx-auto">
                            {videos.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all ${index === currentIndex
                                            ? 'bg-blue-500 scale-125'
                                            : 'bg-white/30 hover:bg-white/50'
                                        }`}
                                />
                            ))}
                        </div>

                        {/* Counter */}
                        <p className="text-center text-white/40 mt-4 text-sm">
                            {currentIndex + 1} / {videos.length}
                        </p>
                    </div>
                </section>

                <Footer />
            </div>
        </div>
    );
}
